/* AI Cube Finder custom element for /pages/cube-finder.
   Owns the search form, talks to the Worker API, renders four result
   states (product, clarify, redirect, error), and handles ATC + analytics.
   Sibling files: sections/scs-cube-finder.liquid, scs-cube-finder.css.

   Cart integration: ATC POSTs to /cart/add.js with line item properties
   _cube_finder_session (UUID) and _cube_finder_query (the original search).
   These ride along to Shopify orders for revenue attribution via the
   post-launch Shopify -> Supabase sync. After ATC, dispatches product:added
   on documentElement so the mini-cart refreshes and opens.

   Analytics: every ATC, View Details, and Browse click POSTs to /click on
   the Worker. Session ID persists across the browser session (sessionStorage).
   Fire-and-forget; failures don't surface. Thumbs up/down + optional
   comment fire POST /feedback. */

const SESSION_STORAGE_KEY = 'scsCubeFinderSessionId';
const REQUEST_TIMEOUT_MS = 30_000;

/* Loading sequence — 5 customer-friendly steps shown during the search
   API call. Steps 1-4 each show for LOADING_STEP_MS; step 5 stays visible
   until the API response arrives (no loop, no pulse). Result render is
   queued so all 5 steps are always shown, even when the API is fast.
   Step copy maps honestly to the actual pipeline (embed → top-K → AI
   reads candidate JSON which includes descriptions, ratings, and prices). */
const LOADING_STEPS = [
  { icon: '🔍', text: 'Reading your request' },
  { icon: '🧩', text: 'Searching our cube catalog' },
  { icon: '📖', text: 'Reading product details' },
  { icon: '⭐', text: 'Checking customer reviews' },
  { icon: '✨', text: 'Picking your cube' },
];
const LOADING_STEP_MS = 1400;

// Pure local pick for the surprise surface (D-02: no immediate repeat).
// Excludes excludeId unless the list only has one item, in which case
// that item is returned anyway — a one-product catalog has no other option.
function pickRandom(products, excludeId) {
  if (products.length === 0) return null;
  if (products.length === 1) return products[0];
  let candidate;
  do {
    candidate = products[Math.floor(Math.random() * products.length)];
  } while (candidate.id === excludeId);
  return candidate;
}

class ScsCubeFinder extends HTMLElement {
  #apiEndpoint;
  #form;
  #input;
  #submitButton;
  #submitText;
  #spinner;
  #chipButtons;
  #resultContainer;

  // Conversation state — clarify carries to the second turn
  #conversationContext = null;

  // Product IDs the customer has rejected via "Want a different suggestion?".
  // Resets on any new typed query / chip click; persists across same-query
  // re-runs and across the clarify chain that produced the visible product.
  #excludedIds = [];

  // Snapshot of the query + conversation context that produced the
  // currently-visible product, used to replay on "different suggestion".
  #lastSearchInputs = null;

  // Current result snapshot for analytics & feedback
  #currentResult = null;

  // Cached session id (loaded lazily — server may also return one)
  #sessionId = null;

  // Loading sequence state
  #loadingStepIndex = 0;
  #loadingStepTimeout = null;
  #pendingResult = null;  // queued render: {data} | {error} | null

  // Surprise-me state — separate surface, own DOM, no shared state with search
  #surpriseContainer;
  #surpriseButton;
  #surpriseResult;
  #surpriseProducts = [];
  #lastSurpriseId = null;
  #surpriseLoaderTimeout = null;

  connectedCallback() {
    this.#apiEndpoint = (this.dataset.apiEndpoint || '').replace(/\/$/, '');
    this.#form = this.querySelector('[data-cube-finder-form]');
    this.#input = this.querySelector('input[name="query"]');
    this.#submitButton = this.querySelector('.scs-cube-finder__submit');
    this.#submitText = this.querySelector('[data-cube-finder-submit-text]');
    this.#spinner = this.querySelector('[data-cube-finder-spinner]');
    this.#chipButtons = Array.from(this.querySelectorAll('[data-cube-finder-chip]'));
    this.#resultContainer = this.querySelector('[data-cube-finder-result]');

    if (!this.#apiEndpoint) {
      console.error('[scs-cube-finder] data-api-endpoint is required');
      return;
    }
    if (!this.#form || !this.#input || !this.#resultContainer) {
      console.error('[scs-cube-finder] required DOM nodes missing');
      return;
    }

    this.#form.addEventListener('submit', (e) => this.#onSubmit(e));
    this.#chipButtons.forEach((btn) => this.#wireChipButton(btn));

    // Carry-forward: if the URL has a ?q=... param (set when arriving
    // from the search-page CTA), pre-fill the input. Don't auto-submit —
    // vague search queries would otherwise jump straight to a clarify
    // question the customer didn't ask for. Let them confirm by clicking
    // Find my cube.
    try {
      const params = new URLSearchParams(window.location.search);
      const incomingQuery = (params.get('q') || '').trim();
      if (incomingQuery && this.#input) {
        this.#input.value = incomingQuery.slice(0, 500);
        // Move focus to the input so the customer sees the pre-filled
        // value and can edit it before submitting.
        this.#input.focus();
        // Place caret at the end so editing is natural
        const len = this.#input.value.length;
        try { this.#input.setSelectionRange(len, len); } catch (e) {}
      }
    } catch (e) {
      // best effort — URL parsing should not break the widget
    }

    // Popular-queries auto-replacement is disabled while the chip set is
    // hand-curated. Analysis of pre-deploy traffic showed the global
    // top-5 popular queries created a feedback loop that funneled ~90% of
    // chip clicks to a single product. Re-enable once cube_finder_popular_queries
    // is rewritten to surface top-per-category instead of global top-5.
    // this.#fetchPopularQueries();

    // Surprise-me: separate surface, sibling to the search card/result.
    // Fetch the eligible list once and cache it; every click after that is
    // a local pick with no network round trip.
    this.#surpriseContainer = this.querySelector('[data-cube-finder-surprise]');
    this.#surpriseButton = this.querySelector('[data-cube-finder-surprise-btn]');
    this.#surpriseResult = this.querySelector('[data-cube-finder-surprise-result]');
    if (this.#surpriseButton) {
      this.#surpriseButton.addEventListener('click', () => this.#handleSurpriseClick());
    }
    this.#fetchSurpriseCatalog();
  }

  #wireChipButton(btn) {
    btn.addEventListener('click', () => {
      this.#input.value = btn.dataset.cubeFinderChip || btn.textContent.trim();
      this.#runSearch(this.#input.value);
    });
  }

  async #fetchPopularQueries() {
    try {
      const response = await fetch(this.#apiEndpoint + '/popular-queries');
      if (!response.ok) return;
      const data = await response.json();
      const queries = Array.isArray(data?.queries) ? data.queries : [];
      if (queries.length === 0) return;
      this.#replaceChips(queries.slice(0, 5));
    } catch (e) {
      // best effort — keep static fallback chips
    }
  }

  #replaceChips(queries) {
    if (!this.#chipButtons[0]) return;
    const container = this.#chipButtons[0].parentElement;
    if (!container) return;
    container.innerHTML = '';
    this.#chipButtons = queries.map((q) => {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'scs-cube-finder__chip';
      btn.dataset.cubeFinderChip = q;
      btn.textContent = q;
      container.appendChild(btn);
      this.#wireChipButton(btn);
      return btn;
    });
  }

  // Session management

  #getSessionId() {
    if (this.#sessionId) return this.#sessionId;
    try {
      const stored = sessionStorage.getItem(SESSION_STORAGE_KEY);
      if (stored) {
        this.#sessionId = stored;
        return stored;
      }
    } catch (e) {
      // sessionStorage unavailable (private mode, etc.) — fall through to generate
    }
    const generated = this.#generateUuid();
    try {
      sessionStorage.setItem(SESSION_STORAGE_KEY, generated);
    } catch (e) {
      // best effort
    }
    this.#sessionId = generated;
    return generated;
  }

  #generateUuid() {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID();
    }
    // RFC4122-ish v4 fallback
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
      const r = (Math.random() * 16) | 0;
      const v = c === 'x' ? r : (r & 0x3) | 0x8;
      return v.toString(16);
    });
  }

  // Search flow

  #onSubmit(event) {
    event.preventDefault();
    const query = (this.#input.value || '').trim();
    if (!query) return;
    this.#runSearch(query);
  }

  async #runSearch(query, opts = {}) {
    // Reset the "rejected products" set unless the caller is replaying the
    // same query through the "different suggestion" flow.
    if (!opts.preserveExcluded) {
      this.#excludedIds = [];
    }
    // Allow callers (different-suggestion replay) to restore a prior
    // conversation context that was cleared on product render.
    if (opts.restoreContext) {
      this.#conversationContext = opts.restoreContext;
    }

    this.#setLoading(true);
    this.#startLoadingSequence();
    this.#currentResult = null;
    this.#pendingResult = null;

    const payload = {
      query,
      session_id: this.#getSessionId(),
    };
    if (this.#conversationContext) {
      payload.conversationContext = this.#conversationContext;
    }
    if (this.#excludedIds.length > 0) {
      payload.exclude_product_ids = this.#excludedIds.slice();
    }

    // Snapshot the inputs that produced this request so the "different
    // suggestion" button can replay the same query + context later, after
    // conversationContext has been cleared on the product render.
    this.#lastSearchInputs = {
      query,
      conversationContext: this.#conversationContext,
    };

    try {
      const data = await this.#post('/search', payload);

      // Server may have generated a session id; persist if so
      if (data.session_id) {
        this.#sessionId = data.session_id;
        try {
          sessionStorage.setItem(SESSION_STORAGE_KEY, data.session_id);
        } catch (e) {
          // best effort
        }
      }

      this.#currentResult = { ...data, query };
      // Queue render; #finishLoadingSequence() invokes #renderResultNow()
      // once step 5 has been shown.
      this.#pendingResult = { kind: 'data', data, query };
      this.#tryRenderPending();
    } catch (err) {
      console.error('[scs-cube-finder] search failed', err);
      this.#pendingResult = { kind: 'error' };
      this.#tryRenderPending();
    }
  }

  // Loading sequence

  #startLoadingSequence() {
    this.#cancelLoadingTimeout();
    this.#loadingStepIndex = 0;
    this.#renderState('loading');
    this.#renderLoadingStep(0);
    // Schedule transitions for steps 2-5 (indices 1-4 in zero-based).
    // Step 5 (index 4) is the terminal step; it stays visible until
    // the API response arrives via #tryRenderPending.
    this.#scheduleNextLoadingStep();
  }

  #scheduleNextLoadingStep() {
    this.#loadingStepTimeout = setTimeout(() => {
      this.#loadingStepIndex += 1;
      if (this.#loadingStepIndex < LOADING_STEPS.length) {
        this.#renderLoadingStep(this.#loadingStepIndex);
        // If we just hit the final step, attempt to flush any pending result
        if (this.#loadingStepIndex === LOADING_STEPS.length - 1) {
          this.#tryRenderPending();
        } else {
          this.#scheduleNextLoadingStep();
        }
      }
    }, LOADING_STEP_MS);
  }

  #renderLoadingStep(index) {
    const step = LOADING_STEPS[index];
    if (!step) return;
    this.#resultContainer.innerHTML = `
      <div class="scs-cube-finder__loading">
        <span class="scs-cube-finder__loading-spinner" aria-hidden="true"></span>
        <div class="scs-cube-finder__loading-step">
          <span class="scs-cube-finder__loading-icon" aria-hidden="true">${step.icon}</span>
          <span class="scs-cube-finder__loading-text">${this.#escape(step.text)}</span>
        </div>
      </div>`;
  }

  #cancelLoadingTimeout() {
    if (this.#loadingStepTimeout) {
      clearTimeout(this.#loadingStepTimeout);
      this.#loadingStepTimeout = null;
    }
  }

  // Called when the API response arrives AND when the loading sequence
  // reaches step 5. Renders only when both conditions are met for a
  // PRODUCT pick. Clarify, redirect, no_match, and error responses
  // short-circuit the sequence — they render immediately so customers
  // don't sit through ~5.6s of theater before seeing a question or a
  // routing message. The full sequence is reserved for the moment when
  // the AI actually picked a cube.
  #tryRenderPending() {
    if (!this.#pendingResult) return;

    const pending = this.#pendingResult;
    const isProductPick =
      pending.kind === 'data' &&
      pending.data?.type === 'product' &&
      pending.data?.product;
    const atFinalStep = this.#loadingStepIndex >= LOADING_STEPS.length - 1;
    if (isProductPick && !atFinalStep) return; // wait for sequence to finish

    this.#cancelLoadingTimeout();
    this.#setLoading(false);

    this.#pendingResult = null;

    if (pending.kind === 'error') {
      this.#renderError('There are too many searches right now. Please try again later.');
      this.#conversationContext = null;
      return;
    }

    const { data, query } = pending;
    if (data.type === 'product' && data.product) {
      this.#renderProduct(data);
      this.#conversationContext = null;
    } else if (data.type === 'clarify') {
      this.#renderClarify(data);
      this.#conversationContext = { originalQuery: query, question: data.question };
    } else if (data.type === 'redirect') {
      this.#renderRedirect(data);
      this.#conversationContext = null;
    } else if (data.type === 'no_match') {
      this.#renderNoMatch(data);
      this.#conversationContext = null;
    } else {
      this.#renderError('Something unexpected came back. Try a different search.');
    }
  }

  // HTTP helpers

  async #post(path, body) {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);
    try {
      const response = await fetch(this.#apiEndpoint + path, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
        signal: controller.signal,
      });
      if (!response.ok) {
        const text = await response.text();
        throw new Error(`HTTP ${response.status}: ${text}`);
      }
      return await response.json();
    } finally {
      clearTimeout(timeout);
    }
  }

  #trackClick(action, productId, variantId) {
    const body = {
      session_id: this.#getSessionId(),
      product_id: productId,
      action,
    };
    if (variantId) body.variant_id = variantId;
    // Fire-and-forget — keepalive lets the request survive a navigation
    try {
      fetch(this.#apiEndpoint + '/click', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
        keepalive: true,
      });
    } catch (e) {
      // best effort
    }
  }

  async #get(path) {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);
    try {
      const response = await fetch(this.#apiEndpoint + path, { signal: controller.signal });
      if (!response.ok) {
        const text = await response.text();
        throw new Error(`HTTP ${response.status}: ${text}`);
      }
      return await response.json();
    } finally {
      clearTimeout(timeout);
    }
  }

  // Surprise me — fetch-once + local pick

  async #fetchSurpriseCatalog() {
    if (!this.#surpriseContainer || !this.#surpriseButton || !this.#surpriseResult) return;
    try {
      const data = await this.#get('/surprise');
      const products = Array.isArray(data?.products) ? data.products : [];
      if (products.length === 0) {
        this.#hideSurprise();
        return;
      }
      this.#surpriseProducts = products;
    } catch (e) {
      // D-10: empty/failed fetch hides the surface, no error, no placeholder
      this.#hideSurprise();
    }
  }

  #hideSurprise() {
    if (!this.#surpriseContainer) return;
    this.#surpriseContainer.dataset.state = 'hidden';
    this.#surpriseContainer.style.display = 'none';
  }

  #handleSurpriseClick() {
    if (this.#surpriseProducts.length === 0) return;
    const picked = pickRandom(this.#surpriseProducts, this.#lastSurpriseId);
    if (!picked) return;
    this.#lastSurpriseId = picked.id;
    // D-01: label doubles as the re-roll affordance after the first pick
    this.#surpriseButton.textContent = 'Surprise me again';

    // Brief simulated loader before the pick reveals — the pick itself is
    // instant (local array pick), so this is cosmetic only. A fresh click
    // cancels any pending reveal so rapid re-rolls don't stack.
    clearTimeout(this.#surpriseLoaderTimeout);
    this.#surpriseResult.innerHTML = `
      <div class="scs-cube-finder__loading">
        <span class="scs-cube-finder__loading-spinner" aria-hidden="true"></span>
        <div class="scs-cube-finder__loading-step">
          <span class="scs-cube-finder__loading-text">Picking a puzzle…</span>
        </div>
      </div>`;
    this.#surpriseLoaderTimeout = setTimeout(() => this.#renderSurpriseProduct(picked), 600);
  }

  #renderSurpriseProduct(p) {
    if (!window.ScsProductCard) {
      console.error('[scs-cube-finder] window.ScsProductCard is not defined — check script load order in layout/theme.liquid');
      this.#hideSurprise();
      return;
    }
    const description = typeof p.description === 'string' ? p.description : '';
    // One URL for image, title, and View details so a click anywhere on the
    // card lands on the same product page (new tab, keeps the finder open).
    const productUrl = this.#withUtm('/products/' + (p.handle || ''), 'surprise_me');
    const cardBody = window.ScsProductCard.render(p, {
      productUrl,
      imgLinkAttr: 'data-cube-finder-surprise-view-media',
      imgAttr: 'data-cube-finder-surprise-img',
      titleLinkAttr: 'data-cube-finder-surprise-view-title',
      atcAttr: 'data-cube-finder-surprise-atc',
      viewAttr: 'data-cube-finder-surprise-view',
      description,
    });

    this.#surpriseResult.innerHTML = `
      <div class="scs-cube-finder__product">${cardBody}
      </div>`;

    const img = this.#surpriseResult.querySelector('[data-cube-finder-surprise-img]');
    if (img) {
      img.addEventListener('error', () => {
        const parent = img.parentElement;
        if (parent) {
          parent.innerHTML =
            '<div class="scs-cube-finder__image-placeholder" aria-hidden="true"></div>';
        }
      });
    }

    const atc = this.#surpriseResult.querySelector('[data-cube-finder-surprise-atc]');
    if (atc) {
      atc.addEventListener('click', () => {
        this.#handleAtc(p, null, atc);
        this.#trackClick('surprise_atc', p.id, p.variantId);
      });
    }

    // View details — the button plus the image and title links all open the
    // product page, so track them all as the same view_details click.
    this.#surpriseResult
      .querySelectorAll('[data-cube-finder-surprise-view], [data-cube-finder-surprise-view-media], [data-cube-finder-surprise-view-title]')
      .forEach((el) => {
        el.addEventListener('click', () => {
          this.#trackClick('view_details', p.id, p.variantId);
        });
      });
  }

  async #submitFeedback(rating, comment) {
    const result = this.#currentResult;
    if (!result) return;
    const body = {
      session_id: this.#getSessionId(),
      query: result.query,
      result_type: result.type,
      rating,
    };
    if (result.product?.id) body.product_id = result.product.id;
    if (result.collection) body.redirect_category = this.#categoryFromCollection(result.collection);
    if (comment) body.comment = comment;
    try {
      await this.#post('/feedback', body);
    } catch (e) {
      console.warn('[scs-cube-finder] feedback failed', e);
    }
  }

  #categoryFromCollection(collection) {
    // Best-effort guess from the collection name. Not load-bearing — server
    // already stored the canonical category in cube_finder_searches.
    if (!collection?.name) return undefined;
    return collection.name.toLowerCase().split(' ')[0];
  }

  // ATC

  /* Three-state button animation:
       idle ("+ Add to cart") → loading (spinner) → success ("✓ Added")
     After ~2s of success state we fade back to idle so the button is
     ready for a second cube (in case the customer wants to keep going).
     If ATC fails, fall back to direct-cart URL navigation so the
     customer's cube isn't lost. */
  async #handleAtc(product, query, buttonEl) {
    if (!product?.variantId) return;

    this.#trackClick('atc', product.id, product.variantId);

    const formData = {
      items: [
        {
          id: Number(product.variantId),
          quantity: 1,
          properties: {
            _cube_finder_session: this.#getSessionId(),
            _cube_finder_query: query || '',
          },
        },
      ],
    };

    if (buttonEl) this.#setAtcState(buttonEl, 'loading');

    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });
      if (!response.ok) {
        const errText = await response.text();
        throw new Error(`/cart/add.js ${response.status}: ${errText}`);
      }
      // Tell the theme to refresh + open the mini-cart drawer
      document.documentElement.dispatchEvent(
        new CustomEvent('product:added', {
          bubbles: true,
          detail: { quantity: 1 },
        })
      );
      if (buttonEl) {
        this.#setAtcState(buttonEl, 'success');
        setTimeout(() => this.#setAtcState(buttonEl, 'idle'), 2200);
      }
    } catch (err) {
      console.error('[scs-cube-finder] ATC failed', err);
      if (buttonEl) this.#setAtcState(buttonEl, 'idle');
      // Fall back to the legacy direct-cart URL so the customer still gets
      // their cube into a cart even if the AJAX path errors.
      window.location.href = `/cart/${product.variantId}:1`;
    }
  }

  #setAtcState(buttonEl, state) {
    // state: 'idle' | 'loading' | 'success'
    buttonEl.dataset.atcState = state;
    buttonEl.disabled = state === 'loading';
    if (state === 'loading') {
      buttonEl.innerHTML =
        '<span class="scs-cube-finder__atc-spinner" aria-hidden="true"></span><span>Adding...</span>';
    } else if (state === 'success') {
      buttonEl.innerHTML =
        '<span aria-hidden="true">✓</span><span>Added to cart</span>';
    } else {
      buttonEl.innerHTML =
        '<span aria-hidden="true">+</span> Add to cart';
    }
  }

  // UI state

  #setLoading(loading) {
    this.#submitButton.disabled = loading;
    this.#input.disabled = loading;
    this.#chipButtons.forEach((btn) => (btn.disabled = loading));
    this.#submitText.hidden = loading;
    this.#spinner.hidden = !loading;
  }

  #renderState(state) {
    this.#resultContainer.dataset.state = state;
  }

  // Renderers

  #renderError(message) {
    this.#renderState('error');
    this.#resultContainer.innerHTML = `
      <div class="scs-cube-finder__error">
        <p>${this.#escape(message)}</p>
      </div>`;
  }

  #renderClarify(data) {
    this.#renderState('clarify');
    const options = Array.isArray(data.options) ? data.options : [];
    const optionsHtml = options
      .map(
        (opt) =>
          `<button type="button" class="scs-cube-finder__option" data-cube-finder-option>${this.#escape(opt)}</button>`
      )
      .join('');
    const optionsBlock = options.length
      ? `<div class="scs-cube-finder__options">${optionsHtml}</div>
         <div class="scs-cube-finder__clarify-or" aria-hidden="true">
           <span class="scs-cube-finder__clarify-or-line"></span>
           <span class="scs-cube-finder__clarify-or-text">or type your own</span>
           <span class="scs-cube-finder__clarify-or-line"></span>
         </div>`
      : '';
    this.#resultContainer.innerHTML = `
      <div class="scs-cube-finder__clarify">
        <p class="scs-cube-finder__eyebrow">Quick question</p>
        <h3 class="scs-cube-finder__question">${this.#escape(data.question || '')}</h3>
        ${optionsBlock}
        <form class="scs-cube-finder__clarify-form" data-cube-finder-clarify-form>
          <input
            type="text"
            class="scs-cube-finder__input scs-cube-finder__clarify-input"
            placeholder="Type your answer (e.g. pyraminx, mid-range 3x3)"
            aria-label="Type your own answer"
            data-cube-finder-clarify-input
            autocomplete="off"
          />
          <button
            type="submit"
            class="scs-cube-finder__submit scs-cube-finder__clarify-submit"
          >
            Send
          </button>
        </form>
      </div>`;

    this.#resultContainer
      .querySelectorAll('[data-cube-finder-option]')
      .forEach((btn) => {
        btn.addEventListener('click', () => {
          const answer = btn.textContent.trim();
          this.#input.value = answer;
          this.#runSearch(answer);
        });
      });

    const form = this.#resultContainer.querySelector('[data-cube-finder-clarify-form]');
    if (form) {
      form.addEventListener('submit', (e) => {
        e.preventDefault();
        const inputEl = form.querySelector('[data-cube-finder-clarify-input]');
        const answer = (inputEl?.value || '').trim();
        if (!answer) return;
        this.#input.value = answer;
        this.#runSearch(answer);
      });
    }
  }

  #renderNoMatch(data) {
    this.#renderState('no_match');
    const message = data.message || "We don't have a match for that.";
    const browse = data.browseCollection || {
      name: 'WCA Cubes & Puzzles',
      url: 'https://speedcubeshop.com/collections/cubes-puzzles-wca',
    };
    this.#resultContainer.innerHTML = `
      <div class="scs-cube-finder__no-match">
        <p class="scs-cube-finder__eyebrow">No exact match</p>
        <p class="scs-cube-finder__no-match-message">${this.#escape(message)}</p>
        <a
          class="scs-cube-finder__cta scs-cube-finder__cta--primary"
          href="${this.#escapeAttr(this.#withUtm(browse.url, 'smart_redirect'))}"
          data-cube-finder-no-match-link
        >
          Browse ${this.#escape(browse.name)}
          <span class="scs-cube-finder__cta-arrow" aria-hidden="true">→</span>
        </a>
      </div>
      ${this.#feedbackMarkup()}`;

    const link = this.#resultContainer.querySelector('[data-cube-finder-no-match-link]');
    if (link) {
      link.addEventListener('click', () => {
        const slug = (browse.url || '').split('/').pop() || 'no_match_browse';
        this.#trackClick('redirect_browse', slug, undefined);
      });
    }
    this.#wireFeedback();
  }

  #renderRedirect(data) {
    this.#renderState('redirect');
    const message = data.message || '';
    const collection = data.collection || {};
    this.#resultContainer.innerHTML = `
      <div class="scs-cube-finder__redirect">
        <p class="scs-cube-finder__eyebrow">Here's where to look</p>
        <p class="scs-cube-finder__redirect-message">${this.#escape(message)}</p>
        <a
          class="scs-cube-finder__cta scs-cube-finder__cta--primary"
          href="${this.#escapeAttr(this.#withUtm(collection.url || '/collections/all', 'smart_redirect'))}"
          data-cube-finder-redirect-link
        >
          Browse ${this.#escape(collection.name || 'collection')}
          <span class="scs-cube-finder__cta-arrow" aria-hidden="true">→</span>
        </a>
      </div>
      ${this.#feedbackMarkup()}`;

    const redirectLink = this.#resultContainer.querySelector('[data-cube-finder-redirect-link]');
    if (redirectLink && data.product?.id == null) {
      redirectLink.addEventListener('click', () => {
        // No product_id on redirects; track using the collection slug
        const slug = (collection.url || '').split('/').pop() || 'unknown';
        this.#trackClick('redirect_browse', slug, undefined);
      });
    }

    this.#wireFeedback();
  }

  #renderProduct(data) {
    if (!window.ScsProductCard) {
      console.error('[scs-cube-finder] window.ScsProductCard is not defined — check script load order in layout/theme.liquid');
      this.#renderError('Something unexpected came back. Try a different search.');
      return;
    }
    this.#renderState('product');
    const p = data.product || {};
    const headline = data.headline || '';
    const explanation = data.explanation || '';
    const isFallback = data.isFallback === true;
    // One URL for image, title, and View details so a click anywhere on the
    // card lands on the same product page (new tab, keeps the finder open).
    const productUrl = this.#withUtm('/products/' + (p.handle || ''), 'smart_recommendation', data.query || data.originalQuery);
    const cardBody = window.ScsProductCard.render(p, {
      productUrl,
      imgLinkAttr: 'data-cube-finder-view-media',
      imgAttr: 'data-cube-finder-img',
      titleLinkAttr: 'data-cube-finder-view-title',
      atcAttr: 'data-cube-finder-atc',
      viewAttr: 'data-cube-finder-view',
      description: explanation,
      descriptionAlways: true,
    });

    this.#resultContainer.innerHTML = `
      <div class="scs-cube-finder__product">
        ${isFallback ? `
          <div class="scs-cube-finder__fallback">
            <span aria-hidden="true">⚠</span>
            <p>No exact match — here's the closest from our in-stock catalog.</p>
          </div>
        ` : ''}
        <div class="scs-cube-finder__product-head">
          <p class="scs-cube-finder__eyebrow">Our pick</p>
          <h3 class="scs-cube-finder__headline">${this.#escape(headline)}</h3>
        </div>${cardBody}
        ${p.id ? `
          <button
            type="button"
            class="scs-cube-finder__try-another"
            data-cube-finder-try-another
          >
            Want a different suggestion?
          </button>
        ` : ''}
      </div>
      ${this.#feedbackMarkup()}`;

    // Image fallback (broken Shopify CDN URLs do happen)
    const img = this.#resultContainer.querySelector('[data-cube-finder-img]');
    if (img) {
      img.addEventListener('error', () => {
        const parent = img.parentElement;
        if (parent) {
          parent.innerHTML =
            '<div class="scs-cube-finder__image-placeholder" aria-hidden="true"></div>';
        }
      });
    }

    // ATC — pass button element so the handler can drive its state animation
    const atc = this.#resultContainer.querySelector('[data-cube-finder-atc]');
    if (atc) {
      atc.addEventListener('click', () =>
        this.#handleAtc(p, data.query || data.originalQuery, atc)
      );
    }

    // View details — the button plus the image and title links all open the
    // product page, so track them all as the same view_details click.
    this.#resultContainer
      .querySelectorAll('[data-cube-finder-view], [data-cube-finder-view-media], [data-cube-finder-view-title]')
      .forEach((el) => {
        el.addEventListener('click', () => {
          this.#trackClick('view_details', p.id, p.variantId);
        });
      });

    // "Want a different suggestion?" — exclude this product and replay.
    const tryAnother = this.#resultContainer.querySelector('[data-cube-finder-try-another]');
    if (tryAnother && p.id) {
      tryAnother.addEventListener('click', () => this.#requestAnotherSuggestion(p.id, p.variantId));
    }

    this.#wireFeedback();
  }

  async #requestAnotherSuggestion(productId, variantId) {
    if (!this.#lastSearchInputs) return;
    if (!this.#excludedIds.includes(productId)) {
      this.#excludedIds.push(productId);
    }
    this.#trackClick('different_suggestion', productId, variantId);
    const { query, conversationContext } = this.#lastSearchInputs;
    await this.#runSearch(query, {
      preserveExcluded: true,
      restoreContext: conversationContext,
    });
  }

  #feedbackMarkup() {
    return `
      <div class="scs-cube-finder__feedback" data-cube-finder-feedback>
        <div class="scs-cube-finder__feedback-prompt">
          <span class="scs-cube-finder__feedback-label">Was this helpful?</span>
          <button type="button" class="scs-cube-finder__feedback-btn" data-cube-finder-feedback-up aria-label="Helpful">
            <span aria-hidden="true">👍</span>
          </button>
          <button type="button" class="scs-cube-finder__feedback-btn" data-cube-finder-feedback-down aria-label="Not helpful">
            <span aria-hidden="true">👎</span>
          </button>
        </div>
        <div class="scs-cube-finder__feedback-down-form" data-cube-finder-feedback-down-form hidden>
          <label class="scs-cube-finder__feedback-comment-label" for="cube-finder-feedback-comment">Tell us what we got wrong (optional)</label>
          <textarea
            id="cube-finder-feedback-comment"
            class="scs-cube-finder__feedback-comment"
            rows="2"
            placeholder="What were you actually looking for?"
            maxlength="500"
          ></textarea>
          <button type="button" class="scs-cube-finder__feedback-submit" data-cube-finder-feedback-submit>Submit feedback</button>
        </div>
        <p class="scs-cube-finder__feedback-thanks" data-cube-finder-feedback-thanks hidden>Thanks for the feedback.</p>
      </div>`;
  }

  #wireFeedback() {
    const root = this.#resultContainer.querySelector('[data-cube-finder-feedback]');
    if (!root) return;
    const upBtn = root.querySelector('[data-cube-finder-feedback-up]');
    const downBtn = root.querySelector('[data-cube-finder-feedback-down]');
    const downForm = root.querySelector('[data-cube-finder-feedback-down-form]');
    const submitBtn = root.querySelector('[data-cube-finder-feedback-submit]');
    const commentInput = root.querySelector('.scs-cube-finder__feedback-comment');
    const thanks = root.querySelector('[data-cube-finder-feedback-thanks]');
    const prompt = root.querySelector('.scs-cube-finder__feedback-prompt');

    const finish = () => {
      if (prompt) prompt.hidden = true;
      if (downForm) downForm.hidden = true;
      if (thanks) thanks.hidden = false;
    };

    if (upBtn) {
      upBtn.addEventListener('click', () => {
        this.#submitFeedback('up', null);
        finish();
      });
    }
    if (downBtn) {
      downBtn.addEventListener('click', () => {
        if (prompt) prompt.hidden = true;
        if (downForm) downForm.hidden = false;
      });
    }
    if (submitBtn) {
      submitBtn.addEventListener('click', () => {
        const comment = (commentInput?.value || '').trim();
        this.#submitFeedback('down', comment || null);
        finish();
      });
    }
  }

  // Escape helpers

  // Append UTM parameters to a destination URL so Shopify Analytics + GA
  // can attribute traffic and (eventually) revenue back to the cube finder.
  //   medium: 'smart_recommendation' for product page clicks,
  //           'smart_redirect' for collection-browse clicks
  //   query:  optional — passed as utm_content so reports can show
  //           which search led to which product view
  #withUtm(url, medium, query) {
    if (!url) return url;
    const params = ['utm_source=cube_finder', `utm_medium=${encodeURIComponent(medium)}`];
    if (query) params.push(`utm_content=${encodeURIComponent(query)}`);
    const joiner = url.includes('?') ? '&' : '?';
    return `${url}${joiner}${params.join('&')}`;
  }

  #escape(str) {
    if (str == null) return '';
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  #escapeAttr(str) {
    return this.#escape(str);
  }
}

if (!customElements.get('scs-cube-finder')) {
  customElements.define('scs-cube-finder', ScsCubeFinder);
}
